import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import 'survey-core/defaultV2.min.css';
import { useNotification } from '../../../hooks';
import { useSurveyJsModel } from '../../../hooks/useSurveyJsModel';
import { useSurveyResponse } from '../../../hooks/useSurveyResponse';
import { PageNotFoundPage } from '../../../shared/components/PageNotFoundPage';
import { TemporaryLoadingPage } from '../TemporaryLoadingPage';
import useUploadDocument from '../hooks/useUploadDocument';
import { useState } from 'react';
import { SurveyResponseComponent } from './SurveyResponseComponent';
import { SubmissionConfirmationModal } from './SubmissionConfirmationModal';
import { useSurvey } from '../../../hooks/useSurvey';
import { useSurveyResponses } from '../../../hooks/useSurveyResponses';

export const SurveyResponsePage = () => {
  const { surveyId } = useParams();
  const {
    data: survey,
    isLoading: isSurveyLoading,
    isFetching: isSurveyFetching,
  } = useSurvey(surveyId);
  const { surveyResponseId } = useParams();
  const { createSurveyResponse, isLoading: isCreateSurveyResponseLoading } =
    useSurveyResponses(surveyId, {
      disableGet: !surveyResponseId,
    });

  const {
    data: surveyResponse,
    isLoading: isSurveyResponseLoading,
    isFetching: isSurveyResponseFetching,
    showConfirmationModal,
    setShowConfirmationModal,
    onSave,
    onSaveAndSubmit,
  } = useSurveyResponse(surveyResponseId);

  const { t } = useTranslation();
  const { showError } = useNotification();
  const [countryCode, setCountryCode] = useState<string | undefined>(
    surveyResponse?.countryCode
  );
  const [countryError, setCountryError] = useState<string | undefined>();

  const surveyToRender = survey || surveyResponse?.survey;
  const surveyModel = useSurveyJsModel(
    surveyToRender?.json,
    surveyResponse?.json,
    surveyResponse?.status
  );
  const isCompleted = surveyResponse?.status === 'COMPLETED';

  /*isLoading from react query will return true even is the query is disabled.
  Since this component will be used for both create and edit, we only return
  the isLoading value for the query that is being used
  */
  const showLoader = createSurveyResponse
    ? isCreateSurveyResponseLoading
    : isSurveyLoading;

  const { attachmentIds } = useUploadDocument(surveyModel);

  const validateResponseJson = () => {
    if (!surveyModel) return;
    const originalData = surveyModel.data;
    surveyModel.clearIncorrectValues(true);
    const correctedJson = surveyModel.data;
    return (
      Object.keys(originalData).length === Object.keys(correctedJson).length
    );
  };

  const handleCountryChange = (value: string | null) => {
    setCountryCode(value || '');
    setCountryError(undefined);
  };

  const save = () => {
    if (!surveyModel) return;
    if (!validateResponseJson() || !countryCode) {
      if (!countryCode) {
        setCountryError(
          t<string>('validations.required', {
            field: t('documents.create.country'),
          })
        );
        showError(t<string>('documents.errors.requiredCountry'));
        return;
      }
      showError(t<string>('documents.errors.responseDoesNotMatchSurvey'));
      return;
    }

    if (!surveyResponseId) {
      const surveyResponseJson = surveyModel.data;
      // Note: this will navigate away on success
      createSurveyResponse.execute({
        json: surveyResponseJson,
        countryCode: countryCode,
        attachmentIds,
      });
    } else {
      onSave({
        countryCode,
        attachmentIds,
        surveyModel,
      });
    }
  };
  const handleSubmit = () => {
    if (!surveyModel) return;
    if (!validateResponseJson() || !countryCode) {
      if (!countryCode) {
        setCountryError(
          t<string>('validations.required', {
            field: t('documents.create.country'),
          })
        );
        showError(t<string>('documents.errors.requiredCountry'));
        return;
      }
      showError(t<string>('documents.errors.responseDoesNotMatchSurvey'));

      return;
    }
    onSaveAndSubmit({
      surveyModel,
      countryCode,
      attachmentIds,
    });
  };

  if (isSurveyFetching || isSurveyResponseFetching)
    return <TemporaryLoadingPage />;
  if (!surveyToRender) return <PageNotFoundPage />;
  if (!surveyModel) return <TemporaryLoadingPage />;

  return (
    <>
      <SurveyResponseComponent
        surveyModel={surveyModel}
        survey={surveyToRender}
        surveyResponse={surveyResponse}
        value={countryCode}
        onChange={handleCountryChange}
        error={countryError}
        canSubmit={!!surveyResponseId}
        defaultCountry={surveyResponse?.countryCode}
        isCompleted={isCompleted}
        onSave={save}
        isLoading={showLoader}
        showConfirmationModal={() => setShowConfirmationModal(true)}
      />
      <SubmissionConfirmationModal
        isOpen={showConfirmationModal}
        onCancel={() => setShowConfirmationModal(false)}
        onConfirm={handleSubmit}
        isLoading={isSurveyResponseLoading}
      />
    </>
  );
};
